import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router-dom'
import GlobalSearch from '@/features/search/GlobalSearch'
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  CreditCard,
  LayoutDashboard,
  LayoutGrid,
  List,
  LogOut,
  Mail,
  Menu,
  Search,
  Settings as SettingsIcon,
  Target,
  UserPlus,
  Users,
  Workflow,
  X,
} from 'lucide-react'
import { useAuthStore } from '@/features/auth/authStore'
import { useSyncSession } from '@/features/auth/useAuth'
import type { AgencyView } from '@/api/types'

/** Grouped, frequency-ordered navigation: most-used sections on top, admin at the bottom. */
const navSections = [
  {
    heading: 'Daily',
    items: [
      { to: '/', label: 'Deals', icon: LayoutGrid, end: true },
      { to: '/focus', label: 'Focus', icon: Target },
    ],
  },
  {
    heading: 'CRM',
    items: [
      { to: '/contacts', label: 'Contacts', icon: Users },
      { to: '/lists', label: 'Lists', icon: List },
    ],
  },
  { heading: 'Insights', items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  {
    heading: 'Engagement & automation',
    items: [
      { to: '/workflows', label: 'Workflows', icon: Workflow },
      { to: '/drip', label: 'Sequences', icon: Mail },
      { to: '/forms', label: 'Forms', icon: ClipboardList },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { to: '/team', label: 'Team', icon: UserPlus, adminOnly: true },
      { to: '/settings', label: 'Settings', icon: SettingsIcon, adminOnly: true },
      { to: '/billing', label: 'Billing', icon: CreditCard, adminOnly: true, agencyOnly: true },
      { to: '/platform', label: 'Platform', icon: Building2, platformOnly: true },
    ],
  },
] as const

const COLLAPSE_KEY = 'pp.sidebarCollapsed'
/** Below Tailwind's `md` the sidebar becomes a slide-in drawer behind a top bar. */
const DESKTOP_QUERY = '(min-width: 768px)'

/** Compact tenant mark for the collapsed rail / logo-less agencies: initial on the brand colour. */
function AgencyMark({ agency }: { agency: AgencyView }) {
  return (
    <span className="w-8 h-8 flex-shrink-0 rounded-lg bg-brand-primary flex items-center justify-center text-base font-bold">
      {agency.name.trim().charAt(0).toUpperCase()}
    </span>
  )
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

/** Human-readable role, e.g. 'AGENCY_ADMIN' → 'Agency admin'. */
function roleLabel(role: string): string {
  const s = role.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Up to two initials for the account avatar, e.g. 'Rusty Gimaev' → 'RG'. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return (first + last).toUpperCase()
}

export default function AppLayout() {
  const user = useAuthStore((s) => s.user)
  const agency = useAuthStore((s) => s.agency)
  const clearSession = useAuthStore((s) => s.clearSession)
  // Refresh name / role / workspace branding from the server (no re-login needed).
  useSyncSession()
  const isAdmin = user?.role === 'AGENCY_ADMIN' || user?.role === 'PLATFORM_ADMIN'
  const isPlatform = user?.role === 'PLATFORM_ADMIN'
  const hasProfile = !!user?.agencyId

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1')
  const [logoFailed, setLogoFailed] = useState(false)
  const toggle = () =>
    setCollapsed((c) => {
      const next = !c
      localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      return next
    })

  // Phone/tablet: the sidebar is a drawer, closed by default — a fixed 224px rail would leave a phone
  // less than half its width for the page.
  const [mobileOpen, setMobileOpen] = useState(false)
  const closeMobile = () => setMobileOpen(false)
  const [searchOpen, setSearchOpen] = useState(false)
  // Ctrl/⌘+K opens the search from anywhere (as in most modern apps).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
  // The drawer always shows the full menu; the icon rail is a desktop preference.
  const rail = collapsed && !mobileOpen

  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileOpen(false)
    // Growing past the breakpoint with the drawer open would leave it open over the desktop layout.
    const desktop = window.matchMedia(DESKTOP_QUERY)
    const onResize = () => desktop.matches && setMobileOpen(false)
    window.addEventListener('keydown', onKey)
    desktop.addEventListener('change', onResize)
    return () => {
      window.removeEventListener('keydown', onKey)
      desktop.removeEventListener('change', onResize)
    }
  }, [mobileOpen])

  const hasLogo = !!agency?.logoUrl && !logoFailed

  return (
    <div className="md:flex min-h-screen">
      {/* Phone/tablet top bar: menu button + the workspace, so the user always knows where they are. */}
      <header className="md:hidden sticky top-0 z-40 h-12 bg-brand-darkest text-white flex items-center gap-3 px-2">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          aria-controls="app-sidebar"
          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10"
        >
          <Menu size={20} />
        </button>
        {agency ? (
          <span className="text-sm font-semibold truncate">{agency.name}</span>
        ) : (
          <img src="/brand/profitpipe-wordmark-white.svg" alt="ProfitPipe" className="h-4" />
        )}
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          aria-label="Search clients and deals"
          className="ml-auto w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/10"
        >
          <Search size={19} />
        </button>
      </header>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/40"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      <aside
        id="app-sidebar"
        className={`w-64 ${collapsed ? 'md:w-16' : 'md:w-56'} bg-brand-darkest text-white flex flex-col fixed inset-y-0 left-0 z-50 md:z-auto transition-[width,transform,visibility] duration-200 md:visible md:translate-x-0 ${
          // invisible: a closed drawer's links must not stay in the keyboard tab order
          mobileOpen ? 'translate-x-0' : 'invisible -translate-x-full'
        }`}
      >
        {/* Brand / tenant header */}
        <div className={`flex items-center gap-2.5 ${rail ? 'justify-center px-2 py-4' : 'p-5'}`}>
          {agency ? (
            rail || !hasLogo ? (
              <AgencyMark agency={agency} />
            ) : (
              // Landscape tenant logo already contains the agency name — show it wide, no separate text.
              <img
                src={agency.logoUrl!}
                alt={`${agency.name} logo`}
                onError={() => setLogoFailed(true)}
                className="h-9 w-auto max-w-full object-contain object-left"
              />
            )
          ) : (
            <img src="/brand/profitpipe-wordmark-white.svg" alt="ProfitPipe" className="h-4" />
          )}
          {/* Fallback agency name when no logo image (expanded only) */}
          {agency && !rail && !hasLogo && (
            <span className="text-base font-semibold leading-tight min-w-0" title={agency.name}>
              {agency.name}
            </span>
          )}
          <button
            type="button"
            onClick={closeMobile}
            aria-label="Close menu"
            className="md:hidden ml-auto w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Find anything — a client by name or phone, a deal by address — from every page. */}
        <div className={rail ? 'px-2 pb-3 flex justify-center' : 'px-3 pb-3'}>
          <button
            type="button"
            onClick={() => {
              closeMobile()
              setSearchOpen(true)
            }}
            title={rail ? 'Search (Ctrl+K)' : undefined}
            aria-label="Search clients and deals"
            className={
              rail
                ? 'w-10 h-10 flex items-center justify-center rounded-lg text-white/70 hover:bg-white/10 hover:text-white'
                : 'w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-sm text-white/70 hover:bg-white/15 hover:text-white'
            }
          >
            <Search size={15} className="shrink-0" />
            {!rail && (
              <>
                <span className="truncate">Search…</span>
                <kbd className="ml-auto hidden md:inline text-[10px] text-white/40 font-sans">{IS_MAC ? '⌘K' : 'Ctrl K'}</kbd>
              </>
            )}
          </button>
        </div>

        <nav className="flex-1 px-2 space-y-1 overflow-y-auto">
          {navSections.map((section) => {
            // billing is a tenant feature — platform admin has no agency; platform console is platform-only
            const items = section.items.filter(
              (n) =>
                (!('adminOnly' in n) || !n.adminOnly || isAdmin) &&
                (!('platformOnly' in n) || isPlatform) &&
                (!('agencyOnly' in n) || !isPlatform),
            )
            if (items.length === 0) return null
            return (
              <div key={section.heading} className="pt-2 first:pt-0">
                {!rail && (
                  <div className="text-[11px] font-medium uppercase tracking-wider text-white/55 px-3 pb-1">
                    {section.heading}
                  </div>
                )}
                {items.map(({ to, label, icon: Icon, ...rest }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={'end' in rest ? rest.end : false}
                    onClick={closeMobile}
                    title={rail ? label : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${rail ? 'justify-center' : ''} ${
                        isActive ? 'bg-white/15' : 'text-white/80 hover:bg-white/10'
                      }`
                    }
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    {!rail && label}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          {/* Who am I / where am I: name, email, role and workspace — so the account is never ambiguous
              (one email can sign into several workspaces). */}
          {/* The card opens My profile (name, photo, signature, password) — except for the platform
              admin, who has no workspace profile. */}
          {rail && user && (
            <Link
              to={hasProfile ? '/me' : '#'}
              onClick={closeMobile}
              className="flex justify-center pb-1"
              title={`${user.fullName} · ${roleLabel(user.role)}${agency ? ` · ${agency.name}` : ''}${hasProfile ? ' — My profile' : ''}`}
            >
              <span className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-xs font-semibold text-white">
                {initials(user.fullName)}
              </span>
            </Link>
          )}
          {!rail && user && (
            <Link
              to={hasProfile ? '/me' : '#'}
              onClick={closeMobile}
              className={`block rounded-xl bg-white/5 px-2.5 py-2.5 mb-1 ${hasProfile ? 'hover:bg-white/10' : 'pointer-events-none'}`}
              title={hasProfile ? `${user.email} — open My profile` : user.email}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-9 h-9 shrink-0 rounded-full bg-brand-primary flex items-center justify-center text-xs font-semibold text-white">
                  {initials(user.fullName)}
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-white truncate">{user.fullName}</div>
                  {agency && (
                    <div className="text-[11px] text-white/50 truncate">{agency.name}</div>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wide rounded-full bg-white/10 text-white/70 px-1.5 py-0.5">
                  {roleLabel(user.role)}
                </span>
                {hasProfile && <span className="text-[11px] text-white/60">My profile ›</span>}
              </div>
            </Link>
          )}
          <button
            onClick={clearSession}
            title={rail ? `Sign out — ${user?.fullName ?? ''}` : undefined}
            className={`flex items-center gap-2 text-sm text-white/70 hover:text-white px-2 ${rail ? 'justify-center w-full' : ''}`}
          >
            <LogOut size={16} className="flex-shrink-0" />
            {!rail && 'Sign out'}
          </button>
          <button
            onClick={toggle}
            title={rail ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`hidden md:flex items-center gap-2 text-sm text-white/50 hover:text-white px-2 ${rail ? 'justify-center w-full' : ''}`}
          >
            {rail ? (
              <ChevronRight size={16} />
            ) : (
              <>
                <ChevronLeft size={16} /> Collapse
              </>
            )}
          </button>
        </div>
      </aside>

      {/* min-w-0: as a flex item, main must not grow past the viewport to fit wide
          content (the board), or the PAGE scrolls horizontally and content slides
          under the fixed sidebar — horizontal scrolling belongs to inner
          overflow-x-auto containers only. */}
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}

      <main
        className={`flex-1 min-w-0 ${collapsed ? 'md:ml-16' : 'md:ml-56'} bg-brand-bg min-h-[calc(100dvh-3rem)] md:min-h-screen transition-[margin] duration-200`}
      >
        <Outlet />
      </main>
    </div>
  )
}
